import { compressImage } from "@/lib/compress-image";
import { ensureRenderable } from "@/lib/heic/ensure-renderable";
import { uploadImageFilename } from "@/lib/upload-filename";

/**
 * Longest edge of every photo an issue update posts. A 4:3 camera frame lands
 * at 1920 × 1440 — about 23% of the 12 MP the camera captures. See
 * [[issue-update-sheet.spec]] § Photos.
 */
export const ISSUE_PHOTO_MAX_PX = 1920;

const ISSUE_PHOTO_QUALITY = 0.9;

/**
 * Turns a picked or captured photo into what an issue update posts: HEIC
 * converted first ([[heic-conversion]]), then capped at
 * {@link ISSUE_PHOTO_MAX_PX} on its longest edge and saved as JPEG.
 *
 * Throws when the file cannot be made renderable — an AVIF, or a HEIC the
 * decoder rejects — so the sheet can leave that photo out and say so. A resize
 * that fails after that point posts the renderable file as it is: losing the
 * size cap is bad, losing the photo is worse.
 */
export async function prepareIssuePhoto(file: File): Promise<File> {
  const renderable = await ensureRenderable(file);
  // The `.jpg` name is what tells `compressImage` to encode JPEG, PNGs included.
  const resized = await compressImage(renderable, "photo.jpg", {
    maxDimension: ISSUE_PHOTO_MAX_PX,
    quality: ISSUE_PHOTO_QUALITY,
  });
  if (resized === renderable) return renderable;
  // The old extension goes: a `.png` name on JPEG bytes would mislabel the
  // upload. The extension is re-derived from what was actually encoded.
  const base = renderable.name.replace(/\.[^.]*$/, "");
  return new File([resized], uploadImageFilename(base, resized.type, "photo"), {
    type: resized.type,
    lastModified: renderable.lastModified,
  });
}
